import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { alpha } from "@mui/material/styles";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import {
  addSkillAgentMoodboardAssets,
  autoSkillAgentMoodboardPages,
  deleteSkillAgentMoodboard,
  deleteSkillAgentMoodboardAsset,
  exportSkillAgentMoodboardPdf,
  fetchSkillAgentMoodboardAssetBlobUrl,
  getSkillAgentMoodboardById,
  getSkillAgentMoodboardPool,
  saveSkillAgentMoodboardPages,
  updateSkillAgentMoodboard,
} from "../../api/Skills/skillAgentMoodboardsApi";
import {
  fetchSkillAgentSearchItemBlobUrl,
  getSkillAgentSearchById,
  listSkillAgentSearches,
} from "../../api/Skills/skillAgentSearchesApi";
import {
  pageFieldSx,
  pagePrimaryButtonSx,
} from "../../styles/pageModernStyles";
import { moodboardStatusLabel } from "./skillAgentTaskTree";
import {
  applyAviatorPresetToPage,
  clampSlot,
  emptyFreePage,
  newSlotId,
  normalizeFreePage,
  normalizePages,
  snapSlot,
  splitCategoryLabel,
  splitStudioLine,
} from "./skillAgentMoodboardLayout";

const HANDLES = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];

function AssetThumb({ assetId, onClick, selected }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let revoked = false;
    let url = "";
    (async () => {
      try {
        url = await fetchSkillAgentMoodboardAssetBlobUrl(assetId);
        if (!revoked) setSrc(url);
      } catch {
        if (!revoked) setSrc("");
      }
    })();
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [assetId]);

  return (
    <Box
      onClick={onClick}
      sx={{
        width: "100%",
        height: "100%",
        bgcolor: "action.hover",
        cursor: onClick ? "pointer" : "default",
        outline: selected ? "2px solid" : "none",
        outlineColor: "primary.main",
        overflow: "hidden",
      }}
    >
      {src ? (
        <Box
          component="img"
          src={src}
          alt=""
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      ) : (
        <Box sx={{ display: "grid", placeItems: "center", height: "100%" }}>
          <CircularProgress size={14} />
        </Box>
      )}
    </Box>
  );
}

function PoolThumb({ item, checked, onToggle, onAdd }) {
  const [src, setSrc] = useState("");

  useEffect(() => {
    let revoked = false;
    let url = "";
    (async () => {
      try {
        url = await fetchSkillAgentSearchItemBlobUrl(item.id);
        if (!revoked) setSrc(url);
      } catch {
        if (!revoked) setSrc("");
      }
    })();
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [item.id]);

  return (
    <Box
      onClick={() => onAdd?.(item)}
      title="Clic para añadir al moodboard"
      sx={{
        position: "relative",
        borderRadius: 1.5,
        overflow: "hidden",
        border: "1px solid",
        borderColor: checked ? "primary.main" : "divider",
        cursor: "pointer",
        bgcolor: "#eee",
        height: 200,
        minHeight: 200,
        maxHeight: 200,
      }}
    >
      <Checkbox
        size="small"
        checked={checked}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onToggle?.(e.target.checked)}
        sx={{
          position: "absolute",
          top: 4,
          left: 4,
          zIndex: 2,
          p: 0.25,
          bgcolor: alpha("#fff", 0.92),
        }}
      />
      {src ? (
        <Box
          component="img"
          src={src}
          alt={item.Title || item.BrandName || ""}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center top",
            display: "block",
          }}
        />
      ) : (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
          }}
        >
          <CircularProgress size={18} />
        </Box>
      )}
      <Typography
        variant="caption"
        noWrap
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
          px: 0.75,
          py: 0.4,
          fontSize: "0.7rem",
          fontWeight: 700,
          color: "#fff",
          bgcolor: "rgba(0,0,0,0.55)",
        }}
      >
        {item.Title || item.BrandName || `#${item.id}`}
      </Typography>
    </Box>
  );
}

function FreeformSlotBox({ slot, selected, onSelect, onChange, canvasRef }) {
  const dragRef = useRef(null);

  const startGesture = (mode, handle, e) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect?.(slot.id);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    dragRef.current = {
      mode,
      handle,
      startX: e.clientX,
      startY: e.clientY,
      orig: { ...slot },
      cw: rect.width,
      ch: rect.height,
    };
    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = (ev.clientX - d.startX) / d.cw;
      const dy = (ev.clientY - d.startY) / d.ch;
      let next = { ...d.orig };
      if (d.mode === "move") {
        next.x = d.orig.x + dx;
        next.y = d.orig.y + dy;
      } else {
        const h = d.handle;
        if (h.includes("e")) next.w = d.orig.w + dx;
        if (h.includes("s")) next.h = d.orig.h + dy;
        if (h.includes("w")) {
          next.x = d.orig.x + dx;
          next.w = d.orig.w - dx;
        }
        if (h.includes("n")) {
          next.y = d.orig.y + dy;
          next.h = d.orig.h - dy;
        }
      }
      onChange?.(
        snapSlot(clampSlot({ ...next, id: slot.id, assetId: slot.assetId })),
      );
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <Box
      onPointerDown={(e) => startGesture("move", null, e)}
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.(slot.id);
      }}
      sx={{
        position: "absolute",
        left: `${slot.x * 100}%`,
        top: `${slot.y * 100}%`,
        width: `${slot.w * 100}%`,
        height: `${slot.h * 100}%`,
        bgcolor: "#ebebeb",
        overflow: "hidden",
        outline: selected ? "2px solid" : "1px solid rgba(0,0,0,0.08)",
        outlineColor: selected ? "primary.main" : undefined,
        outlineOffset: -1,
        cursor: "move",
        zIndex: selected ? 5 : 1,
        touchAction: "none",
        userSelect: "none",
      }}
    >
      {slot.assetId ? (
        <AssetThumb assetId={slot.assetId} />
      ) : (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "grid",
            placeItems: "center",
            height: "100%",
            fontSize: "0.65rem",
            pointerEvents: "none",
          }}
        >
          vacía
        </Typography>
      )}
      {selected
        ? HANDLES.map((h) => (
            <Box
              key={h}
              onPointerDown={(e) => startGesture("resize", h, e)}
              sx={{
                position: "absolute",
                width: 10,
                height: 10,
                bgcolor: "primary.main",
                border: "1px solid #fff",
                borderRadius: 0.25,
                zIndex: 6,
                ...(h.includes("n") ? { top: -5 } : null),
                ...(h.includes("s") ? { bottom: -5 } : null),
                ...(h.includes("w") ? { left: -5 } : null),
                ...(h.includes("e") ? { right: -5 } : null),
                ...(h === "n" || h === "s"
                  ? { left: "50%", marginLeft: "-5px" }
                  : null),
                ...(h === "e" || h === "w"
                  ? { top: "50%", marginTop: "-5px" }
                  : null),
                cursor: `${h}-resize`,
              }}
            />
          ))
        : null}
    </Box>
  );
}

async function loadPoolFromSearches(board) {
  const customerId = board?.CustomerId ? Number(board.CustomerId) : null;
  const temporada = String(board?.Temporada || "").trim();
  const list = await listSkillAgentSearches({
    ...(customerId ? { customerId } : {}),
    ...(temporada ? { temporada } : {}),
  });
  const searches = (Array.isArray(list) ? list : []).filter((s) => {
    if (customerId && Number(s.CustomerId) !== customerId) return false;
    if (temporada && String(s.Temporada || "").trim() !== temporada) {
      return false;
    }
    return true;
  });
  const items = [];
  for (const s of searches) {
    const detail = await getSkillAgentSearchById(s.id);
    const imgs = (Array.isArray(detail?.items) ? detail.items : []).filter(
      (it) => !it.Kind || String(it.Kind).toLowerCase() === "image",
    );
    for (const it of imgs) {
      items.push({
        id: it.id,
        SearchId: s.id,
        Title: it.Title,
        BrandName: it.BrandName,
        BrandId: it.BrandId,
        SourceUrl: it.SourceUrl,
        MimeType: it.MimeType,
        DriveFileUrl: it.DriveFileUrl,
        SearchTitulo: s.Titulo || null,
      });
    }
  }
  return {
    searches: searches.map((s) => ({
      id: s.id,
      Titulo: s.Titulo,
      Status: s.Status,
      Temporada: s.Temporada,
      CustomerId: s.CustomerId,
    })),
    items,
  };
}

export default function SkillAgentMoodboardDetail({
  moodboardId,
  onChanged,
  onDeleted,
}) {
  const canvasRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [board, setBoard] = useState(null);
  const [assets, setAssets] = useState([]);
  const [pages, setPages] = useState([emptyFreePage()]);
  const [pageIdx, setPageIdx] = useState(0);
  const [activeSlotId, setActiveSlotId] = useState(null);
  const [pool, setPool] = useState({ searches: [], items: [] });
  const [poolPick, setPoolPick] = useState(() => new Set());
  const [poolError, setPoolError] = useState("");
  const [poolLoading, setPoolLoading] = useState(false);
  const [poolShowCount, setPoolShowCount] = useState(24);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [titulo, setTitulo] = useState("");
  const [categoryLabel, setCategoryLabel] = useState("");
  const [studioLine, setStudioLine] = useState("");

  const load = useCallback(async () => {
    if (!moodboardId) return;
    setLoading(true);
    setError("");
    setPoolError("");
    try {
      const data = await getSkillAgentMoodboardById(moodboardId);
      setBoard(data);
      setAssets(Array.isArray(data?.assets) ? data.assets : []);
      setPages(normalizePages(data?.PagesJson));
      setTitulo(data?.Titulo || "");
      setCategoryLabel(data?.CategoryLabel || "");
      setStudioLine(data?.StudioLine || "");
      setActiveSlotId(null);
      setPoolShowCount(24);

      setPoolLoading(true);
      let poolData = { searches: [], items: [] };
      try {
        poolData = await getSkillAgentMoodboardPool(moodboardId);
      } catch (poolErr) {
        try {
          poolData = await loadPoolFromSearches(data);
        } catch {
          setPoolError(
            poolErr?.response?.data?.error ||
              "No se pudo cargar el pool de SEARCH.",
          );
          poolData = { searches: [], items: [] };
        }
      }
      if (
        !Array.isArray(poolData?.items) ||
        (!poolData.items.length && data?.CustomerId)
      ) {
        try {
          const fallback = await loadPoolFromSearches(data);
          if (fallback.items?.length) poolData = fallback;
        } catch {
          /* keep primary */
        }
      }
      setPool({
        searches: Array.isArray(poolData?.searches) ? poolData.searches : [],
        items: Array.isArray(poolData?.items) ? poolData.items : [],
      });
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo cargar el moodboard.");
    } finally {
      setPoolLoading(false);
      setLoading(false);
    }
  }, [moodboardId]);

  useEffect(() => {
    load();
  }, [load]);

  const usedSearchItemIds = useMemo(() => {
    const s = new Set();
    for (const a of assets) {
      if (a.SearchItemId) s.add(Number(a.SearchItemId));
    }
    return s;
  }, [assets]);

  const currentPage = useMemo(
    () => normalizeFreePage(pages[pageIdx] || emptyFreePage()),
    [pages, pageIdx],
  );

  const applyBoard = (data) => {
    if (!data) return;
    setBoard(data);
    if (Array.isArray(data.assets)) setAssets(data.assets);
    if (data.PagesJson) setPages(normalizePages(data.PagesJson));
    if (data.Titulo != null) setTitulo(data.Titulo);
    if (data.CategoryLabel != null) setCategoryLabel(data.CategoryLabel);
    if (data.StudioLine != null) setStudioLine(data.StudioLine);
  };

  const updateCurrentPageSlots = (updater) => {
    setPages((prev) =>
      prev.map((p, i) => {
        if (i !== pageIdx) return p;
        const base = normalizeFreePage(p);
        const slots =
          typeof updater === "function" ? updater(base.slots) : updater;
        return { slots: slots.map((s) => clampSlot(s)) };
      }),
    );
  };

  const handleSaveMeta = async () => {
    setBusy(true);
    setError("");
    try {
      const data = await updateSkillAgentMoodboard(moodboardId, {
        Titulo: titulo,
        CategoryLabel: categoryLabel,
        StudioLine: studioLine,
      });
      applyBoard(data);
      onChanged?.();
      setOkMsg("Guardado.");
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo guardar.");
    } finally {
      setBusy(false);
    }
  };

  const handleSavePages = async (nextPages) => {
    const payload = (nextPages || pages).map((p) => normalizeFreePage(p));
    setBusy(true);
    setError("");
    try {
      const data = await saveSkillAgentMoodboardPages(moodboardId, payload);
      applyBoard(data);
      onChanged?.();
      setOkMsg("Páginas guardadas.");
    } catch (err) {
      setError(
        err?.response?.data?.error || "No se pudieron guardar las páginas.",
      );
    } finally {
      setBusy(false);
    }
  };

  const assignAssetToSlot = (slotId, assetId) => {
    updateCurrentPageSlots((slots) =>
      slots.map((s) =>
        s.id === slotId ? { ...s, assetId: Number(assetId) } : s,
      ),
    );
  };

  const placeAssetInFirstEmpty = (assetId) => {
    setPages((prev) => {
      const next = prev.map((p) => normalizeFreePage(p));
      for (let i = 0; i < next.length; i += 1) {
        const empty = next[i].slots.find((s) => s.assetId == null);
        if (empty) {
          empty.assetId = Number(assetId);
          setPageIdx(i);
          setActiveSlotId(empty.id);
          return next;
        }
      }
      const page = emptyFreePage();
      page.slots[0].assetId = Number(assetId);
      setPageIdx(next.length);
      setActiveSlotId(page.slots[0].id);
      return [...next, page];
    });
  };

  const handleAddSlot = () => {
    const slot = clampSlot({
      id: newSlotId(),
      x: 0.35,
      y: 0.35,
      w: 0.28,
      h: 0.3,
      assetId: null,
    });
    updateCurrentPageSlots((slots) => [...slots, slot]);
    setActiveSlotId(slot.id);
  };

  const handleRemoveSlot = () => {
    if (!activeSlotId) return;
    updateCurrentPageSlots((slots) => {
      if (slots.length <= 1) return slots;
      return slots.filter((s) => s.id !== activeSlotId);
    });
    setActiveSlotId(null);
  };

  const handleAviatorPreset = () => {
    setPages((prev) =>
      prev.map((p, i) =>
        i === pageIdx ? applyAviatorPresetToPage(p) : normalizeFreePage(p),
      ),
    );
    setActiveSlotId(null);
  };

  const placeAddedAssets = (addedList, basePages, startPageIdx) => {
    const added = Array.isArray(addedList) ? addedList : [];
    if (!added.length) return basePages;
    let pagesNext = (basePages || []).map((p) => normalizeFreePage(p));
    let pi = startPageIdx;
    const already = new Set(
      pagesNext.flatMap((p) =>
        p.slots.map((s) => s.assetId).filter((id) => id != null),
      ),
    );
    for (const asset of added) {
      const id = Number(asset.id);
      if (already.has(id)) continue;
      let placed = false;
      const active = pagesNext[pi]?.slots?.find(
        (s) => s.id === activeSlotId && s.assetId == null,
      );
      if (active) {
        active.assetId = id;
        already.add(id);
        placed = true;
      }
      if (!placed) {
        for (let i = 0; i < pagesNext.length; i += 1) {
          const empty = pagesNext[i].slots.find((s) => s.assetId == null);
          if (empty) {
            empty.assetId = id;
            already.add(id);
            pi = i;
            placed = true;
            break;
          }
        }
      }
      if (!placed) {
        const page = emptyFreePage();
        page.slots[0].assetId = id;
        already.add(id);
        pagesNext = [...pagesNext, page];
        pi = pagesNext.length - 1;
      }
    }
    setPageIdx(pi);
    return pagesNext;
  };

  const handleAddFromPool = async (idsOverride) => {
    const rawIds = Array.isArray(idsOverride) ? idsOverride : [...poolPick];
    const ids = rawIds
      .map((id) => Number(id))
      .filter((id) => id && !usedSearchItemIds.has(id));
    if (!ids.length) return;
    setBusy(true);
    setError("");
    try {
      const data = await addSkillAgentMoodboardAssets(moodboardId, {
        searchItemIds: ids,
      });
      const added = Array.isArray(data?.added) ? data.added : [];
      const serverPages = normalizePages(data?.PagesJson);
      const placedOnServer = added.every((a) =>
        serverPages.some((p) =>
          p.slots.some((s) => Number(s.assetId) === Number(a.id)),
        ),
      );
      if (Array.isArray(data?.assets)) setAssets(data.assets);
      setBoard(data);
      if (placedOnServer) {
        setPages(serverPages);
      } else {
        setPages(placeAddedAssets(added, serverPages, pageIdx));
      }
      setPoolPick(new Set());
      onChanged?.();
      setOkMsg(`${added.length || ids.length} foto(s) añadidas del SEARCH.`);
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudieron añadir.");
    } finally {
      setBusy(false);
    }
  };

  const handleAddOneFromPool = async (item) => {
    if (!item?.id || busy) return;
    await handleAddFromPool([item.id]);
  };

  const availablePoolItems = useMemo(
    () =>
      (pool.items || []).filter((it) => !usedSearchItemIds.has(Number(it.id))),
    [pool.items, usedSearchItemIds],
  );

  const handleFillPageFromPool = async () => {
    const emptyCount = currentPage.slots.filter(
      (s) => s.assetId == null,
    ).length;
    const take = availablePoolItems
      .slice(0, Math.max(emptyCount, 7))
      .map((it) => Number(it.id));
    if (!take.length) return;
    await handleAddFromPool(take);
  };

  const handleUpload = async (fileList) => {
    const files = [...(fileList || [])];
    if (!files.length) return;
    setBusy(true);
    setError("");
    try {
      const data = await addSkillAgentMoodboardAssets(moodboardId, { files });
      const added = Array.isArray(data?.added) ? data.added : [];
      const serverPages = normalizePages(data?.PagesJson);
      const placedOnServer = added.every((a) =>
        serverPages.some((p) =>
          p.slots.some((s) => Number(s.assetId) === Number(a.id)),
        ),
      );
      if (Array.isArray(data?.assets)) setAssets(data.assets);
      setBoard(data);
      if (placedOnServer) setPages(serverPages);
      else setPages(placeAddedAssets(added, serverPages, pageIdx));
      onChanged?.();
      setOkMsg(`${files.length} subida(s).`);
    } catch (err) {
      setError(err?.response?.data?.error || "Error al subir.");
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveAsset = async (assetId) => {
    setBusy(true);
    try {
      const data = await deleteSkillAgentMoodboardAsset(moodboardId, assetId);
      applyBoard(data);
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo quitar.");
    } finally {
      setBusy(false);
    }
  };

  const handleAutoPages = async () => {
    setBusy(true);
    try {
      const data = await autoSkillAgentMoodboardPages(moodboardId);
      applyBoard(data);
      setPageIdx(0);
      setActiveSlotId(null);
      onChanged?.();
      setOkMsg("Páginas repartidas (Aviator 7 / página).");
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo repartir.");
    } finally {
      setBusy(false);
    }
  };

  const handleExport = async () => {
    if (exporting || busy) return;
    setExporting(true);
    setBusy(true);
    setError("");
    setOkMsg("");
    try {
      const payload = pages.map((p) => normalizeFreePage(p));
      const saved = await saveSkillAgentMoodboardPages(moodboardId, payload);
      applyBoard(saved);
      await updateSkillAgentMoodboard(moodboardId, {
        Titulo: titulo,
        CategoryLabel: categoryLabel,
        StudioLine: studioLine,
      });
      const data = await exportSkillAgentMoodboardPdf(moodboardId, {
        customerFolderName: board?.CustomerName || undefined,
      });
      applyBoard(data.moodboard || data.search || data);

      if (data.pdfBase64) {
        try {
          const bin = atob(data.pdfBase64);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
          const blob = new Blob([bytes], { type: "application/pdf" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = data.pdfFileName || "moodboard.pdf";
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 2000);
        } catch {
          /* download optional */
        }
      }

      onChanged?.();
      if (data.drivePdfFileUrl) {
        setOkMsg("PDF exportado y subido a Drive (también descargado).");
      } else if (data.driveNote) {
        setOkMsg(`PDF descargado. Drive: ${data.driveNote}`);
      } else {
        setOkMsg("PDF generado y descargado.");
      }
    } catch (err) {
      setError(err?.response?.data?.error || "Error al exportar PDF.");
    } finally {
      setExporting(false);
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await deleteSkillAgentMoodboard(moodboardId);
      setDeleteOpen(false);
      onDeleted?.(moodboardId);
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo eliminar.");
      setBusy(false);
    }
  };

  const catParts = splitCategoryLabel(categoryLabel);
  const studioParts = splitStudioLine(studioLine);

  if (loading && !board) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!board) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Moodboard no encontrado.
      </Alert>
    );
  }

  return (
    <Stack spacing={2} sx={{ p: { xs: 2, md: 2.5 } }}>
      <Backdrop
        open={exporting}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          bgcolor: alpha("#0f172a", 0.55),
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <CircularProgress size={48} sx={{ color: "#fff" }} />
        <Typography fontWeight={800} sx={{ color: "#fff", fontSize: 18 }}>
          Exportando PDF…
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
          Guardando páginas y generando el archivo
        </Typography>
      </Backdrop>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={1.5}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" fontWeight={800} noWrap>
              {board.Titulo}
            </Typography>
            <Chip
              size="small"
              label={moodboardStatusLabel(board.Status)}
              color={board.Status === "ready" ? "success" : "default"}
              sx={{ height: 24, fontWeight: 800 }}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {[board.CustomerName, board.Temporada].filter(Boolean).join(" · ")}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {board.DrivePdfFileUrl ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNewOutlinedIcon />}
              href={board.DrivePdfFileUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              PDF Drive
            </Button>
          ) : null}
          <Button
            size="small"
            variant="contained"
            startIcon={
              exporting ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <PictureAsPdfOutlinedIcon />
              )
            }
            disabled={busy || exporting || !assets.length}
            onClick={handleExport}
            sx={pagePrimaryButtonSx}
          >
            {exporting ? "Exportando…" : "Exportar PDF"}
          </Button>
          <Button
            size="small"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            disabled={busy}
            onClick={() => setDeleteOpen(true)}
          >
            Eliminar
          </Button>
        </Stack>
      </Stack>

      {error ? (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}
      {okMsg ? (
        <Alert severity="success" onClose={() => setOkMsg("")}>
          {okMsg}
        </Alert>
      ) : null}

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
        <TextField
          size="small"
          label="Título"
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          fullWidth
          sx={pageFieldSx}
        />
        <TextField
          size="small"
          label="Etiqueta (barra título)"
          placeholder="Aviator - JKT"
          value={categoryLabel}
          onChange={(e) => setCategoryLabel(e.target.value)}
          fullWidth
          sx={pageFieldSx}
        />
        <Button
          size="small"
          variant="outlined"
          disabled={busy}
          onClick={handleSaveMeta}
          sx={{ flexShrink: 0, textTransform: "none", fontWeight: 700 }}
        >
          Guardar
        </Button>
      </Stack>
      <TextField
        size="small"
        label="Studio / contacto (barra inferior)"
        value={studioLine}
        onChange={(e) => setStudioLine(e.target.value)}
        fullWidth
        sx={pageFieldSx}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 420px" },
          gap: 2,
        }}
      >
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
            flexWrap="wrap"
            useFlexGap
            spacing={0.5}
          >
            <Tabs
              value={pageIdx}
              onChange={(_, v) => {
                setPageIdx(v);
                setActiveSlotId(null);
              }}
              variant="scrollable"
              scrollButtons="auto"
            >
              {pages.map((_, i) => (
                <Tab key={i} label={`Pág. ${i + 1}`} sx={{ minHeight: 36 }} />
              ))}
            </Tabs>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                sx={{ textTransform: "none" }}
                onClick={() => {
                  setPages((p) => [
                    ...p.map(normalizeFreePage),
                    emptyFreePage(),
                  ]);
                  setPageIdx(pages.length);
                  setActiveSlotId(null);
                }}
              >
                + Página
              </Button>
              <Button
                size="small"
                sx={{ textTransform: "none" }}
                onClick={handleAddSlot}
              >
                + Casilla
              </Button>
              <Button
                size="small"
                sx={{ textTransform: "none" }}
                disabled={!activeSlotId || currentPage.slots.length <= 1}
                onClick={handleRemoveSlot}
              >
                Quitar casilla
              </Button>
              <Button
                size="small"
                sx={{ textTransform: "none" }}
                onClick={handleAviatorPreset}
              >
                Plantilla Aviator
              </Button>
              <Button
                size="small"
                sx={{ textTransform: "none" }}
                disabled={busy || !assets.length}
                onClick={handleAutoPages}
              >
                Auto 7/pág
              </Button>
              <Button
                size="small"
                variant="outlined"
                sx={{ textTransform: "none", fontWeight: 700 }}
                disabled={busy}
                onClick={() => handleSavePages()}
              >
                Guardar páginas
              </Button>
            </Stack>
          </Stack>

          <Box
            sx={{
              position: "relative",
              bgcolor: "#fff",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: "12px",
              width: "100%",
              maxWidth: 920,
              mx: "auto",
              aspectRatio: "297 / 210",
            }}
          >
            <Box
              ref={canvasRef}
              onClick={() => setActiveSlotId(null)}
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                overflow: "hidden",
                bgcolor: "#f3f3f3",
              }}
            >
              {currentPage.slots.map((slot) => (
                <FreeformSlotBox
                  key={slot.id}
                  slot={slot}
                  selected={activeSlotId === slot.id}
                  canvasRef={canvasRef}
                  onSelect={setActiveSlotId}
                  onChange={(next) => {
                    updateCurrentPageSlots((slots) =>
                      slots.map((s) => (s.id === next.id ? next : s)),
                    );
                  }}
                />
              ))}

              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  bgcolor: "#383838",
                  color: "#fff",
                  px: 1.75,
                  py: 0.75,
                  zIndex: 8,
                  pointerEvents: "none",
                  maxWidth: "38%",
                  display: "flex",
                  alignItems: "baseline",
                  gap: 0.5,
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 800,
                    fontSize: "0.85rem",
                    lineHeight: 1.2,
                    color: "#fff",
                  }}
                >
                  {catParts.main || "Moodboard"}
                </Typography>
                {catParts.sub ? (
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 400,
                      fontSize: "0.8rem",
                      lineHeight: 1.2,
                      color: "rgba(255,255,255,0.72)",
                    }}
                  >
                    - {catParts.sub}
                  </Typography>
                ) : null}
              </Box>

              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  bgcolor: "#383838",
                  color: "#fff",
                  px: 1.5,
                  py: 0.85,
                  zIndex: 8,
                  pointerEvents: "none",
                  maxWidth: "42%",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: "0.72rem",
                    lineHeight: 1.2,
                    color: "#8cd4e0",
                  }}
                >
                  {studioParts.studioName}
                </Typography>
                {studioParts.contactLine ? (
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: "0.58rem",
                      lineHeight: 1.35,
                      color: "#fff",
                      mt: 0.25,
                    }}
                  >
                    {studioParts.contactLine}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1, textAlign: "center" }}
          >
            Arrastra y redimensiona las casillas. Cada página puede ser
            distinta.
            {activeSlotId ? " Casilla seleccionada." : ""}
          </Typography>
        </Box>

        <Stack spacing={1.5}>
          <Typography variant="subtitle2" fontWeight={800}>
            En el moodboard ({assets.length})
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 0.75,
              maxHeight: 200,
              overflow: "auto",
            }}
          >
            {assets.map((a) => (
              <Box key={a.id} sx={{ position: "relative", height: 72 }}>
                <AssetThumb
                  assetId={a.id}
                  selected={false}
                  onClick={() => {
                    if (activeSlotId) assignAssetToSlot(activeSlotId, a.id);
                    else placeAssetInFirstEmpty(a.id);
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => handleRemoveAsset(a.id)}
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    bgcolor: alpha("#fff", 0.85),
                    p: 0.25,
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))}
          </Box>

          <Typography variant="subtitle2" fontWeight={800}>
            Añadir fotos
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Del SEARCH (abajo) o desde tu PC con <b>Subir archivo</b>. La subida
            del PC solo entra en este moodboard (no en el SEARCH).
          </Typography>
          <Button
            size="small"
            component="label"
            variant="contained"
            disabled={busy}
            startIcon={<CloudUploadOutlinedIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              alignSelf: "stretch",
            }}
          >
            Subir archivo desde el PC
            <input
              type="file"
              hidden
              accept="image/*"
              multiple
              onChange={(e) => {
                handleUpload(e.target.files);
                e.target.value = "";
              }}
            />
          </Button>

          <Typography variant="subtitle2" fontWeight={800} sx={{ pt: 0.5 }}>
            Pool SEARCH ({availablePoolItems.length})
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Clic en una miniatura = añadir al moodboard.
          </Typography>
          {poolError ? (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              {poolError}
            </Alert>
          ) : null}
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              variant="outlined"
              disabled={busy || poolPick.size === 0}
              onClick={() => handleAddFromPool()}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Añadir marcadas {poolPick.size || ""}
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={busy || availablePoolItems.length === 0}
              onClick={handleFillPageFromPool}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Llenar huecos
            </Button>
          </Stack>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 1,
              maxHeight: 560,
              minHeight: 220,
              overflow: "auto",
              alignContent: "start",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1.5,
              p: 1,
              bgcolor: alpha("#000", 0.02),
            }}
          >
            {poolLoading ? (
              <Box
                sx={{
                  gridColumn: "1 / -1",
                  display: "grid",
                  placeItems: "center",
                  py: 3,
                }}
              >
                <CircularProgress size={22} />
              </Box>
            ) : (
              availablePoolItems.slice(0, poolShowCount).map((it) => (
                <PoolThumb
                  key={it.id}
                  item={it}
                  checked={poolPick.has(Number(it.id))}
                  onAdd={handleAddOneFromPool}
                  onToggle={(on) => {
                    setPoolPick((prev) => {
                      const next = new Set(prev);
                      if (on) next.add(Number(it.id));
                      else next.delete(Number(it.id));
                      return next;
                    });
                  }}
                />
              ))
            )}
          </Box>
          {!poolLoading && availablePoolItems.length > poolShowCount ? (
            <Button
              size="small"
              sx={{ textTransform: "none" }}
              onClick={() => setPoolShowCount((n) => n + 24)}
            >
              Ver más ({availablePoolItems.length - poolShowCount} restantes)
            </Button>
          ) : null}
          {!poolLoading && !availablePoolItems.length ? (
            <Typography variant="caption" color="text.secondary">
              No quedan fotos de SEARCH por añadir. Usa{" "}
              <b>Subir archivo desde el PC</b> o lanza otro SEARCH.
            </Typography>
          ) : null}
        </Stack>
      </Box>

      <Dialog
        open={deleteOpen}
        onClose={() => !busy && setDeleteOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>Eliminar moodboard</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Se borra del CRM y el PDF/carpeta de Drive van a la papelera.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={busy}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy}
            onClick={handleDelete}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
